/**
 * The optimistic CRUD + MD import shared by Brand memory and Personal memory.
 * Both lists have the same shape ({ memories, cap }), the same routes under a
 * base path, and the same rules: the list reflects the user's action at once,
 * only the touched row rolls back on failure (with the server's words in a
 * toast — "Memory is full (30/30)" is the one users actually need to read), and
 * the list refetches once the last concurrent mutation settles.
 */
import {
  useMutation,
  useQueryClient,
  type QueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { nanoid } from "nanoid";
import { axiosInstance } from "@/lib/api";
import { queue } from "@/components/Toast";

export type MemoryBase = {
  id: string;
  body: string;
  source: string;
  createdAt: string;
  updatedAt: string;
};

/** What a caller fills in on an optimistic row: everything but id, body and timestamps. */
export type MemoryExtras<M extends MemoryBase> = Omit<M, "id" | "body" | "createdAt" | "updatedAt">;

export type MemoryList<M extends MemoryBase> = {
  memories: M[];
  /** The per-owner hard cap, sent by the server so the UI never hardcodes it. */
  cap: number;
};

export type MemoryImportCandidate = {
  body: string;
  /** The extractor's advice; the user decides. Recommended ones start ticked. */
  recommended: boolean;
  /** Why it is not recommended; null when it is. */
  reason: string | null;
};

export type MemoryImportPreview = {
  candidates: MemoryImportCandidate[];
  cap: number;
  /** Free slots right now. Saving more than this is refused whole. */
  headroom: number;
};

export type MemoryImportFile = { name: string; content: string };

/** Mirrors backend utils/enums/MemoryImport.ts — checked client-side before the call. */
export const MEMORY_IMPORT_MAX_FILES = 50;
export const MEMORY_IMPORT_MAX_CHARS = 60_000;

const TEMP_PREFIX = "temp-";

/** A row the server has not confirmed yet: shown, but not editable. */
export function isPendingMemory(memory: MemoryBase): boolean {
  return memory.id.startsWith(TEMP_PREFIX);
}

export function memoryErrorMessage(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    return (err.response?.data as { message?: string })?.message ?? err.message ?? fallback;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

function toastError(title: string, err: unknown) {
  queue.add({ title, description: memoryErrorMessage(err, "Please try again."), variant: "error" });
}

function writeList<M extends MemoryBase>(
  qc: QueryClient,
  key: QueryKey,
  update: (memories: M[]) => M[],
) {
  qc.setQueryData<MemoryList<M>>(key, (prev) =>
    prev ? { ...prev, memories: update(prev.memories) } : prev,
  );
}

function tempMemory<M extends MemoryBase>(body: string, extra: MemoryExtras<M>): M {
  const now = new Date().toISOString();
  return {
    ...extra,
    // nanoid rather than crypto.randomUUID, which is missing outside secure contexts.
    id: `${TEMP_PREFIX}${nanoid()}`,
    body,
    createdAt: now,
    updatedAt: now,
  } as M;
}

/**
 * Every mutation on one list shares a mutationKey, so a refetch only fires
 * once the last in-flight one settles — an earlier refetch would briefly bring
 * back rows whose deletes are still in flight. The settling one counts itself.
 */
function settle(qc: QueryClient, key: QueryKey) {
  if (qc.isMutating({ mutationKey: [...key, "mutate"] }) > 1) return;
  void qc.invalidateQueries({ queryKey: key });
}

type Target = {
  /** The list's query key. */
  key: QueryKey;
  /** e.g. `/brand-memories/<brandId>` or `/user-memories`; null disables the calls. */
  basePath: string | null;
};

export function useCreateMemory<M extends MemoryBase>(
  { key, basePath }: Target,
  optimistic: MemoryExtras<M>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: [...key, "mutate"],
    mutationFn: async (body: string) => {
      const res = await axiosInstance.post<M>(basePath!, { body });
      return res.data;
    },
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: key });
      const temp = tempMemory<M>(body, optimistic);
      writeList<M>(qc, key, (list) => [...list, temp]);
      return { tempId: temp.id };
    },
    onSuccess: (memory, _body, context) => {
      writeList<M>(qc, key, (list) => list.map((m) => (m.id === context?.tempId ? memory : m)));
    },
    onError: (err, _body, context) => {
      writeList<M>(qc, key, (list) => list.filter((m) => m.id !== context?.tempId));
      toastError("Could not save this memory", err);
    },
    onSettled: () => settle(qc, key),
  });
}

export function useUpdateMemory<M extends MemoryBase>({ key, basePath }: Target) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: [...key, "mutate"],
    mutationFn: async ({ id, body }: { id: string; body: string }) => {
      const res = await axiosInstance.patch<M>(`${basePath}/${id}`, { body });
      return res.data;
    },
    onMutate: async ({ id, body }) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<MemoryList<M>>(key)?.memories.find((m) => m.id === id);
      writeList<M>(qc, key, (list) =>
        list.map((m) => (m.id === id ? { ...m, body, updatedAt: new Date().toISOString() } : m)),
      );
      return { previous };
    },
    onSuccess: (memory) => {
      writeList<M>(qc, key, (list) => list.map((m) => (m.id === memory.id ? memory : m)));
    },
    onError: (err, { id }, context) => {
      const previous = context?.previous;
      if (previous) writeList<M>(qc, key, (list) => list.map((m) => (m.id === id ? previous : m)));
      toastError("Could not save this memory", err);
    },
    onSettled: () => settle(qc, key),
  });
}

export function useDeleteMemory<M extends MemoryBase>({ key, basePath }: Target) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: [...key, "mutate"],
    mutationFn: async (id: string) => {
      await axiosInstance.delete(`${basePath}/${id}`);
      return id;
    },
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: key });
      const list = qc.getQueryData<MemoryList<M>>(key)?.memories ?? [];
      const index = list.findIndex((m) => m.id === id);
      writeList<M>(qc, key, (current) => current.filter((m) => m.id !== id));
      return { removed: index >= 0 ? { memory: list[index], index } : null };
    },
    onError: (err, _id, context) => {
      const removed = context?.removed;
      if (removed) {
        writeList<M>(qc, key, (list) => {
          const next = [...list];
          next.splice(Math.min(removed.index, next.length), 0, removed.memory);
          return next;
        });
      }
      toastError("Could not delete this memory", err);
    },
    onSettled: () => settle(qc, key),
  });
}

/** One LLM call that reads the files into candidates. Writes nothing. */
export function usePreviewMemoryImport({ basePath }: Pick<Target, "basePath">) {
  return useMutation({
    mutationFn: async (files: MemoryImportFile[]) => {
      const res = await axiosInstance.post<MemoryImportPreview>(`${basePath}/import/preview`, {
        files,
      });
      return res.data;
    },
  });
}

/** Saves the picked candidates, all or none — the rows appear at once. */
export function useSaveMemoryImport<M extends MemoryBase>(
  { key, basePath }: Target,
  optimistic: MemoryExtras<M>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: [...key, "mutate"],
    mutationFn: async (bodies: string[]) => {
      const res = await axiosInstance.post<{ memories: M[] }>(`${basePath}/import`, { bodies });
      return res.data.memories;
    },
    onMutate: async (bodies) => {
      await qc.cancelQueries({ queryKey: key });
      const temps = bodies.map((body) => tempMemory<M>(body, optimistic));
      writeList<M>(qc, key, (list) => [...list, ...temps]);
      return { tempIds: new Set(temps.map((t) => t.id)) };
    },
    onSuccess: (memories, _bodies, context) => {
      writeList<M>(qc, key, (list) => [
        ...list.filter((m) => !context?.tempIds.has(m.id)),
        ...memories,
      ]);
      queue.add({
        title: `Imported ${memories.length} ${memories.length === 1 ? "memory" : "memories"}`,
        variant: "success",
      });
    },
    onError: (err, _bodies, context) => {
      writeList<M>(qc, key, (list) => list.filter((m) => !context?.tempIds.has(m.id)));
      toastError("Could not import these memories", err);
    },
    onSettled: () => settle(qc, key),
  });
}
